import { useCallback, useEffect, useRef, useState } from "react";
import {
  beginPatientChunkedAudioUploadSession,
  getPatientAudio,
  startAudioProcessing,
} from "../../../../../../services/api-documentation";
import { mixpanel } from "../../../../../../utils/mixpanel";
import { TSession } from "../../../../../../utils/types";
import { RecordingUploadQueue } from "../services/recordingUploadQueue";
import recordingSessionService from "../services/recordingSessionService";
import { saveChunkToDisk } from "../services/localChunkExport";
import {
  ChunkedRecordingResult,
  RecordingSessionRecord,
} from "../types/recording";

type UseChunkedRecorderParams = {
  session: TSession | null;
  clinicId: string;
  patientId: string;
  patientName?: string;
  onCompleted: (result: ChunkedRecordingResult) => void;
  onError?: (message: string) => void;
};

const SEGMENT_DURATION_SECONDS = 30;
const SEGMENT_ROTATION_MS = SEGMENT_DURATION_SECONDS * 1000;
const MIN_CHUNK_BYTES = 1024;
type ChunkedAudioFormat = "webm" | "m4a";

const CHUNKED_MIME_CANDIDATES: Array<{
  mimeType: string;
  audioFormat: ChunkedAudioFormat;
}> = [
  { mimeType: "audio/webm;codecs=opus", audioFormat: "webm" },
  { mimeType: "audio/webm", audioFormat: "webm" },
  { mimeType: "audio/mp4;codecs=mp4a.40.2", audioFormat: "m4a" },
  { mimeType: "audio/mp4", audioFormat: "m4a" },
];

const pickChunkedMimeType = (): {
  mimeType: string;
  audioFormat: ChunkedAudioFormat;
} | null => {
  if (
    typeof MediaRecorder === "undefined" ||
    typeof MediaRecorder.isTypeSupported !== "function"
  ) {
    return null;
  }

  for (const candidate of CHUNKED_MIME_CANDIDATES) {
    if (MediaRecorder.isTypeSupported(candidate.mimeType)) {
      return candidate;
    }
  }

  return null;
};

export const useChunkedRecorder = ({
  session,
  clinicId,
  patientId,
  patientName,
  onCompleted,
  onError,
}: UseChunkedRecorderParams) => {
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingStep, setProcessingStep] = useState("");

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const queueRef = useRef<RecordingUploadQueue | null>(null);
  const sessionRef = useRef<RecordingSessionRecord | null>(null);
  const partCounterRef = useRef(1);
  const lastPartPromiseRef = useRef<Promise<void> | null>(null);
  const noteTypeRef = useRef("");
  const noteDateRef = useRef<string | undefined>(undefined);
  const stopRequestedRef = useRef(false);
  const segmentRotationTimerRef = useRef<number | null>(null);
  const isSegmentRotationRef = useRef(false);
  const recordingStartedAtRef = useRef<number | null>(null);
  const recordingDurationSecondsRef = useRef<number | null>(null);
  const totalAudioBytesRef = useRef(0);

  const clearSegmentRotationTimer = useCallback(() => {
    if (segmentRotationTimerRef.current !== null) {
      window.clearTimeout(segmentRotationTimerRef.current);
      segmentRotationTimerRef.current = null;
    }
  }, []);
  const resetRuntimeState = useCallback(() => {
    clearSegmentRotationTimer();
    isSegmentRotationRef.current = false;
    mediaRecorderRef.current = null;
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    queueRef.current = null;
    sessionRef.current = null;
    partCounterRef.current = 1;
    lastPartPromiseRef.current = null;
    noteTypeRef.current = "";
    noteDateRef.current = undefined;
    recordingStartedAtRef.current = null;
    recordingDurationSecondsRef.current = null;
    totalAudioBytesRef.current = 0;
  }, [clearSegmentRotationTimer]);

  const getRecordingDurationSeconds = useCallback(() => {
    if (recordingDurationSecondsRef.current !== null) {
      return recordingDurationSecondsRef.current;
    }

    if (recordingStartedAtRef.current === null) {
      return 0;
    }

    return Math.max(
      0,
      Math.round((Date.now() - recordingStartedAtRef.current) / 1000),
    );
  }, []);

  const trackRecordingFailure = useCallback(
    (
      errorStage: string,
      error: unknown,
      recordingSession: RecordingSessionRecord | null = sessionRef.current,
    ) => {
      mixpanel.track("Voice Recording Error", {
        "User Email": session?.userEmail,
        "Clinic Id": clinicId,
        "Clinic Name": session?.clinicName,
        "Patient ID": patientId,
        "Patient Name": patientName,
        "Error Message": error instanceof Error ? error.message : String(error),
        "Error Stage": errorStage,
        "Note Type": noteTypeRef.current || undefined,
        "Recording Duration": getRecordingDurationSeconds(),
        "Audio Size (bytes)": totalAudioBytesRef.current,
        "Chunk Count": Math.max(partCounterRef.current - 1, 0),
        "Audio ID": recordingSession?.audioId,
        "Upload Session ID": recordingSession?.uploadSessionId,
        "Upload Mode": "chunked_resumable",
        "Device Type": navigator.userAgent || "Unknown",
        "Is Online": navigator.onLine,
      });
    },
    [clinicId, getRecordingDurationSeconds, patientId, patientName, session],
  );

  const finalizeAndProcess = useCallback(async () => {
    if (!session || !sessionRef.current || !queueRef.current) {
      trackRecordingFailure(
        "finalize_state",
        "Missing chunked recording state during finalization",
      );
      onError?.(
        "Recording could not be finalized because its upload state was lost.",
      );
      return;
    }

    const recordingSession = sessionRef.current;
    const finalPartNumber = Math.max(partCounterRef.current - 1, 0);

    if (!finalPartNumber) {
      await recordingSessionService.updateSession(recordingSession.id, {
        status: "failed",
        lastError: "No audio captured for chunked session",
      });
      trackRecordingFailure(
        "no_audio_captured",
        "No audio captured for chunked session",
        recordingSession,
      );
      onError?.("No audio data captured. Please try again.");
      return;
    }

    setIsProcessing(true);
    setProcessingStep("Uploading remaining audio segments...");

    try {
      await recordingSessionService.updateSession(recordingSession.id, {
        status: "finalizing",
        finalPartNumber,
      });

      await queueRef.current.finalize(finalPartNumber);
      // Upload is complete once finalize succeeds; clear local recovery data immediately.
      await recordingSessionService.deleteSessionAndParts(recordingSession.id);

      setProcessingStep("Processing voice note...");
      const processed = await startAudioProcessing(
        session,
        recordingSession.audioId,
        patientId,
        noteTypeRef.current,
        recordingSession.appointmentId,
        noteDateRef.current,
      );

      const patientAudio = await getPatientAudio(
        session,
        clinicId,
        patientId,
        true,
      );
      const audioMetadata = patientAudio.metadata.find(
        (item) => item.id === recordingSession.audioId,
      );

      const result: ChunkedRecordingResult = {
        transcription: processed.transcription,
        summary: processed.summary,
        audioUrl: audioMetadata?.get_url || "",
        audioId: recordingSession.audioId,
        noteType: noteTypeRef.current,
      };

      mixpanel.track("Voice Recording Completed", {
        "User Email": session.userEmail,
        "Clinic Id": clinicId,
        "Clinic Name": session.clinicName,
        "Patient ID": patientId,
        "Patient Name": patientName,
        "Recording Duration": getRecordingDurationSeconds(),
        "Audio Size (bytes)": totalAudioBytesRef.current,
        "Chunk Count": finalPartNumber,
        "Audio ID": recordingSession.audioId,
        "Upload Session ID": recordingSession.uploadSessionId,
        "Upload Mode": "chunked_resumable",
        "Note Type": noteTypeRef.current,
      });

      onCompleted(result);
    } catch (error) {
      await recordingSessionService.updateSession(recordingSession.id, {
        status: "stopped",
        finalPartNumber,
        lastError: error instanceof Error ? error.message : String(error),
      });

      trackRecordingFailure("finalize_and_process", error, recordingSession);

      onError?.(
        navigator.onLine
          ? "Recording was saved, but upload could not be finalized. It will retry when connection is stable."
          : "You are offline. Segments were saved and will resume uploading automatically.",
      );
    } finally {
      setIsProcessing(false);
      setProcessingStep("");
      resetRuntimeState();
    }
  }, [
    clinicId,
    getRecordingDurationSeconds,
    onCompleted,
    onError,
    patientId,
    patientName,
    resetRuntimeState,
    session,
    trackRecordingFailure,
  ]);

  const startRecording = useCallback(
    async (params: {
      noteType: string;
      noteDate?: string;
      appointmentId?: number | null;
    }) => {
      if (!session) {
        trackRecordingFailure("missing_session", "Missing session");
        onError?.("Missing session. Please refresh and try again.");
        return;
      }

      const normalizedNoteType = params.noteType.trim();
      if (!normalizedNoteType) {
        trackRecordingFailure("validate_note_type", "Missing note type");
        onError?.("Please choose a note type.");
        return;
      }

      try {
        setProcessingStep("");
        stopRequestedRef.current = false;

        const chunkedMime = pickChunkedMimeType();
        if (!chunkedMime) {
          trackRecordingFailure(
            "browser_support",
            "Chunked recording is not supported in this browser",
          );
          onError?.(
            "Chunked recording is not supported in this browser. Please switch to standard recording.",
          );
          return;
        }
        if (process.env.NODE_ENV !== "production") {
          console.info("[ChunkedRecorder] Selected mimeType", {
            mimeType: chunkedMime.mimeType,
            audioFormat: chunkedMime.audioFormat,
          });
        }

        const uploadSession = await beginPatientChunkedAudioUploadSession(
          session,
          patientId,
          "surgeon_audio",
          params.noteDate,
          SEGMENT_DURATION_SECONDS,
          chunkedMime.audioFormat,
        );

        const localSession = await recordingSessionService.createSession({
          clinicId,
          patientId,
          patientName,
          noteType: normalizedNoteType,
          noteDate: params.noteDate,
          appointmentId: params.appointmentId,
          audioId: uploadSession.audioId,
          uploadMode: "chunked_resumable",
          uploadSessionId: uploadSession.uploadSessionId,
          segmentDurationSeconds: uploadSession.segmentDurationSeconds,
          recommendedPresignBatchSize:
            uploadSession.recommendedPresignBatchSize || 10,
          nextPartNumber: uploadSession.nextPartNumber,
          status: "recording",
        });

        const queue = new RecordingUploadQueue({
          session,
          recordingSession: localSession,
          batchSize: uploadSession.recommendedPresignBatchSize,
        });
        await queue.initialize();

        const stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            sampleRate: 44100,
            channelCount: 1,
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
          },
        });

        partCounterRef.current = uploadSession.nextPartNumber;
        noteTypeRef.current = normalizedNoteType;
        noteDateRef.current = params.noteDate;
        sessionRef.current = localSession;
        queueRef.current = queue;
        streamRef.current = stream;

        const createMediaRecorder = (
          sourceStream: MediaStream,
        ): MediaRecorder =>
          new MediaRecorder(sourceStream, {
            mimeType: chunkedMime.mimeType,
            audioBitsPerSecond: 128000,
          });

        const scheduleSegmentRotation = () => {
          clearSegmentRotationTimer();
          segmentRotationTimerRef.current = window.setTimeout(() => {
            const activeRecorder = mediaRecorderRef.current;
            if (
              stopRequestedRef.current ||
              !activeRecorder ||
              activeRecorder.state !== "recording"
            ) {
              return;
            }
            isSegmentRotationRef.current = true;
            activeRecorder.stop();
          }, SEGMENT_ROTATION_MS);
        };

        const startSegmentRecorder = (sourceStream: MediaStream) => {
          const recorder = createMediaRecorder(sourceStream);

          recorder.ondataavailable = async (event: BlobEvent) => {
            const activeSession = sessionRef.current;
            if (!event.data || event.data.size <= 0 || !activeSession) {
              return;
            }
            if (event.data.size < MIN_CHUNK_BYTES) {
              if (process.env.NODE_ENV !== "production") {
                console.info("[ChunkedRecorder] Skipping tiny chunk", {
                  bytes: event.data.size,
                });
              }
              return;
            }

            totalAudioBytesRef.current += event.data.size;

            const currentPart = partCounterRef.current;
            partCounterRef.current += 1;
            const sessionId = activeSession.id;

            const persistPromise = (async () => {
              await recordingSessionService.storePart(
                sessionId,
                currentPart,
                event.data,
              );
              await saveChunkToDisk({
                partNumber: currentPart,
                blob: event.data,
                audioFormat: chunkedMime.audioFormat,
                mimeType: chunkedMime.mimeType,
              });

              await recordingSessionService.updateSession(sessionId, {
                nextPartNumber: partCounterRef.current,
              });

              try {
                await queueRef.current?.enqueuePart(currentPart);
              } catch (error) {
                // Keep local part persisted; upload will retry on reconnect/recovery.
                await recordingSessionService.updatePart(
                  sessionId,
                  currentPart,
                  {
                    status: "failed",
                    lastError:
                      error instanceof Error ? error.message : String(error),
                  },
                );
              }
            })();

            lastPartPromiseRef.current = persistPromise;
            await persistPromise;
          };

          recorder.onstop = async () => {
            clearSegmentRotationTimer();

            if (stopRequestedRef.current) {
              recordingDurationSecondsRef.current =
                getRecordingDurationSeconds();
              setIsRecording(false);
              if (lastPartPromiseRef.current) {
                try {
                  await lastPartPromiseRef.current;
                } catch {
                  // Ignore local persistence errors here; finalize will surface them.
                }
              }
              await finalizeAndProcess();
              return;
            }

            if (!isSegmentRotationRef.current) {
              recordingDurationSecondsRef.current =
                getRecordingDurationSeconds();
              setIsRecording(false);
              trackRecordingFailure(
                "unexpected_stop",
                "Recording stopped unexpectedly",
                sessionRef.current,
              );
              onError?.("Recording stopped unexpectedly. Please try again.");
              return;
            }

            isSegmentRotationRef.current = false;
            if (!streamRef.current) {
              return;
            }

            if (lastPartPromiseRef.current) {
              try {
                await lastPartPromiseRef.current;
              } catch {
                // Ignore local persistence errors here; finalize will surface them.
              }
            }
            startSegmentRecorder(streamRef.current);
          };

          mediaRecorderRef.current = recorder;
          recorder.start();
          scheduleSegmentRotation();
        };

        recordingStartedAtRef.current = Date.now();
        recordingDurationSecondsRef.current = null;
        totalAudioBytesRef.current = 0;
        startSegmentRecorder(stream);
        setIsRecording(true);

        mixpanel.track("Voice Recording Started", {
          "User Email": session.userEmail,
          "Clinic Id": clinicId,
          "Clinic Name": session.clinicName,
          "Patient ID": patientId,
          "Patient Name": patientName,
          "Device Type": navigator.userAgent || "Unknown",
          "Has ClinicOS UUID": !!patientId,
          "Note Type": normalizedNoteType,
          "Audio ID": localSession.audioId,
          "Upload Session ID": localSession.uploadSessionId,
          "Upload Mode": "chunked_resumable",
        });
      } catch (error) {
        trackRecordingFailure("start_recording", error);
        onError?.(
          error instanceof Error
            ? error.message
            : "Failed to start robust recording.",
        );
        resetRuntimeState();
      }
    },
    [
      clinicId,
      clearSegmentRotationTimer,
      finalizeAndProcess,
      getRecordingDurationSeconds,
      onError,
      patientId,
      patientName,
      resetRuntimeState,
      session,
      trackRecordingFailure,
    ],
  );

  const stopRecording = useCallback(async () => {
    const recorder = mediaRecorderRef.current;
    if (!recorder || recorder.state !== "recording") {
      return;
    }
    try {
      recorder.requestData();
    } catch {
      // Best-effort flush; ignore if unsupported.
    }
    clearSegmentRotationTimer();
    isSegmentRotationRef.current = false;
    stopRequestedRef.current = true;
    recorder.stop();
  }, [clearSegmentRotationTimer]);

  useEffect(() => {
    const handleOnline = () => {
      if (!queueRef.current) return;
      queueRef.current.flush().catch(() => {
        // Recovery path will retry; no-op here to avoid interrupting recording UX.
      });
    };

    window.addEventListener("online", handleOnline);
    return () => {
      window.removeEventListener("online", handleOnline);
    };
  }, []);

  useEffect(() => {
    return () => {
      if (mediaRecorderRef.current?.state === "recording") {
        mediaRecorderRef.current.stop();
      }
      resetRuntimeState();
    };
  }, [resetRuntimeState]);

  return {
    isRecording,
    isProcessing,
    processingStep,
    startRecording,
    stopRecording,
  };
};
